import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string, date } from 'yup';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { sub } from 'date-fns';
import States from '../../../Helpers/Lists/States';
import { InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput } from '../../../Helpers/Inputs';
import GenderButtons from '../../Common/SubComponents/GenderButtons';
import TobaccoRadio from '../../Common/SubComponents/TobaccoRadio'
import BackNext from '../../Common/SubComponents/BackNext';
import CrutchIcon from '../../../../icons/crutch.svg';
import BandAidIcon from '../../../../icons/band-aid.svg';
import DollarShieldIcon from '../../../../icons/dollar-shield.svg';
import ButtonInputGroup from '../ButtonInput/ButtonInputGroup';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';


import '../../Common/BasicInfo.scss';
import './BasicInfo.scss';

export default function BasicInfo(props) {

  const insuranceTypes = [
    {
      label: 'Long Term',
      icon: CrutchIcon,
      descr: 'A comprehensive, flexible solution that can provide monthly benefits up to $12,000 per month.',
      tobaccoVerbiage: {
        label: 'Have you used any form of tobacco or nicotine?',
        yes: 'Currently or previously used',
        no: 'Never used'
      },
    },
    {
      label: 'Short Term',
      icon: BandAidIcon,
      descr: 'Immediate coverage that can fill the protection gap until long-term benefits kick in.',
      tobaccoVerbiage: {
        label: 'Have you used any form of tobacco or nicotine?',
        yes: 'Currently or previously used',
        no: 'Never used'
      },
    },
    {
      label: 'Lump Sum',
      icon: DollarShieldIcon,
      descr: 'Pays a single lump sum benefit for a total disability that is expected to last at least one year.',
      tobaccoVerbiage: {
        label: 'Tobacco Use',
        yes: 'Yes, I use tobacco products',
        no: 'No, I do not use tobacco products'
      }
    }];

  const { register, errors, handleSubmit, setValue } = useForm({
    validationSchema: BasicInfo.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
  };

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'insuranceType' });
    setValue('insuranceType', props.formState.insuranceType);
    register({ name: 'state' });
    setValue('state', props.formState.state);
    register({ name: 'gender' });
    setValue('gender', props.formState.gender);
    register({ name: 'birthdate' });
    setValue('birthdate', props.formState.birthdate);
    register({ name: 'tobacco' });
    setValue('tobacco', props.formState.tobacco);
  }, []);

  const onNext = () => {
    // Save formState to basicinfo
    var basicInfoFromSessionStorage = getBasicInfo();
    basicInfoFromSessionStorage.gender = props.formState.gender;
    basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
    basicInfoFromSessionStorage.state = props.formState.state;
    basicInfoFromSessionStorage.tobacco = props.formState.tobacco;

    setBasicInfo(basicInfoFromSessionStorage);

    props.onNext();
  }


  return (
    <div className="basic-info basic-info-disability d-flex justify-content-center">
      <Form onSubmit={handleSubmit(onNext)}>
        <ButtonInputGroup data={insuranceTypes} value={props.formState.insuranceType} onClick={(e) => handleValueChange('insuranceType', e)} />

        <div className="form-fields">
          <p className="descr mb-3">{insuranceTypes[props.formState.insuranceType].descr}</p>

          <GenderButtons
            selectedGender={props.formState.gender}
            onClickMale={() => handleValueChange('gender', 'M')}
            onClickFemale={() => handleValueChange('gender', 'F')}
            error={errors.gender} />

          <Form.Group>
            <TextInput
              name="birthdate"
              id="birthdate"
              label="Date of Birth"
              value={props.formState.birthdate}
              errors={errors && errors.birthdate}
              inputRef={props.register}
              onChange={(e) => handleValueChange('birthdate', e.target.value)}
              InputProps={{
                inputComponent: InputMaskDate,
              }}
            />
          </Form.Group>

          <Form.Group className="mb-0 ml-3">
            <TobaccoRadio
              tobacco={props.formState.tobacco}
              onChange={(e) => handleValueChange('tobacco', e)}
              error={errors.tobacco}
              inputRef={register({ required: true })}
              verbiage={insuranceTypes[props.formState.insuranceType].tobaccoVerbiage}
            />
          </Form.Group>

          <Form.Group>
            <SelectInput
              name="state"
              id="state"
              value={props.formState.state}
              onChange={(e) => handleValueChange('state', e.target.value)}
              label="State"
              selectOptions={States()}
              errors={errors && errors.state}
            />
          </Form.Group>

          <BackNext
            hideBack={props.hideBack}
            onBack={props.onBack}
          />
        </div>
      </Form>
    </div>
  );
}

BasicInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  hideBack: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired
};

BasicInfo.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Minimum age is ${ageSpecs.minAgeDays ? ageSpecs.minAgeDays + ' days' : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    tobacco: string().required('This field is required.'),
  })
};

BasicInfo.DefaultFormState = (prefill = false, existingBasicInfo = null) => {
  return {
    insuranceType: 0,
    gender: prefill ? 'M' : (existingBasicInfo && existingBasicInfo.gender) || '',
    birthdate: prefill ? '11/15/1970' : (existingBasicInfo && existingBasicInfo.birthdate) || '',
    tobacco: prefill ? 'false' : (existingBasicInfo && existingBasicInfo.tobacco) || '',
    state: prefill ? 'ID' : (existingBasicInfo && existingBasicInfo.state) || '',
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Disability/steps/BasicInfo.js